import React, {
  ReactNode,
  ReactElement,
} from 'react';
import {
  Control,
  Controller,
  ControllerProps,
  ControllerRenderProps,
  FieldPath,
  FieldValues,
  UseFormStateReturn,
  ControllerFieldState,
} from 'react-hook-form';

type RenderProps<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = {
  field: Omit<ControllerRenderProps<TFieldValues, TName>, 'ref'>;
  fieldState: ControllerFieldState;
  formState: UseFormStateReturn<TFieldValues>;
};

type Props<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
> = Omit<ControllerProps<TFieldValues, TName>, 'render' | 'control'> & {
    render: (props: RenderProps<TFieldValues, TName>) => ReactNode;
    control?: Control<TFieldValues>;
  };

export { Controller };

export default function FormController<
  TFieldValues extends FieldValues = FieldValues,
  TName extends FieldPath<TFieldValues> = FieldPath<TFieldValues>,
>({
  render,
  control,
  ...props
}: Props<TFieldValues, TName>) {
  return (
    <Controller
      {...props}
      control={control}
      render={(
        { field: {
          ref, ...field
        }, ...remainder },
      ) => render({ field, ...remainder }) as ReactElement}
    />
  );
}
